import React from 'react';
import { Text, StyleSheet } from 'react-native';
import { GlassCard } from '@/components/shared/redesign';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

export interface FormSectionThemeColors {
  surface: string;
  titleText: string;
  subtitleText: string;
}

interface FormSectionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  style?: any;
  /**
   * Optional override of the section's surface + text colors. When omitted
   * the section uses the shared admin glass treatment driven by theme tokens.
   * Kept as an escape hatch for legacy call sites that pass bespoke palettes.
   */
  themeColors?: FormSectionThemeColors;
}

export const FormSection: React.FC<FormSectionProps> = ({
  title,
  subtitle,
  children,
  style,
  themeColors,
}) => {
  const styles = useStyles();

  // Filter out empty strings and whitespace-only text nodes
  const validChildren = React.Children.toArray(children).filter((child) => {
    if (typeof child === 'string') {
      return child.trim() !== '';
    }
    return true;
  });

  if (themeColors) {
    return (
      <GlassCard padding={0} style={[styles.cardWrapper, { backgroundColor: themeColors.surface }, style]}>
        <Text style={[styles.title, { color: themeColors.titleText }]}>{title}</Text>
        {subtitle ? (
          <Text style={[styles.subtitle, { color: themeColors.subtitleText }]}>{subtitle}</Text>
        ) : null}
        {validChildren}
      </GlassCard>
    );
  }

  return (
    <GlassCard padding={0} style={[styles.cardWrapper, style]}>
      <Text style={[styles.title, styles.titleColor]}>{title}</Text>
      {subtitle ? <Text style={[styles.subtitle, styles.subtitleColor]}>{subtitle}</Text> : null}
      {validChildren}
    </GlassCard>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) => {
    const admin = components.admin;
    return StyleSheet.create({
      cardWrapper: {
        marginBottom: 16,
        padding: 16,
      },
      title: {
        fontSize: 18,
        fontWeight: '600',
        marginBottom: 4,
      },
      subtitle: {
        fontSize: 14,
        lineHeight: 20,
        marginBottom: 8,
      },
      titleColor: {
        color: admin.dataTable.text,
      },
      subtitleColor: {
        color: colors.text.dim,
      },
    });
  });
